import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import { logger } from '../../utils/log';

/**
 * usePanorama3DOverlay — THE panorama-page 3D-overlay state machine (F-2 cluster pattern;
 * App destructures the return under the original names, so consumers stay unchanged).
 *
 * Owns the overlay lifecycle atoms (on/off flag, source, opacity), the DERIVED visibility,
 * and the invariant that used to be enforced nowhere: the stored flag and the derived
 * visibility must never diverge. Divergence was a shipped bug (2026-07-13): unrelated flows
 * can kill an overlay "leg" (hide the Autodesk viewer / clear its config) while the flag
 * stays true — the E57 viewer then got overlayMode=false (nav discs returned mid-overlay),
 * the 3D button read "off", and the old toggle (keyed on the DERIVED value) could never
 * clear the stale flag. The reconciler effect below clears the flag whenever the flagged
 * source can no longer actually show (or the user left the panorama page — the pre-existing
 * page-leave behavior, folded in).
 *
 * Deliberately NOT owned here: the ON-activation side effects (building the Autodesk
 * config, toggling viewer visibility). Those mutate App-owned viewer state whose
 * declarations live after this hook's call site — App's toggle handler resolves activation
 * and then calls `openPanorama3DOverlay(source)`.
 *
 * The decision logic is exported as pure functions (derivePanorama3DOverlayVisibility,
 * shouldClearOverlayFlag) and pinned by unit tests in __tests__/usePanorama3DOverlay.test.ts.
 */

export type Panorama3DOverlaySource = 'autodesk' | 'ifc';

// 40% for RVT and IFC alike (Idan, 2026-09-28; was 60%). A starting value only — see openPanorama3DOverlay.
export const DEFAULT_PANORAMA_3D_OVERLAY_OPACITY = 0.4;
// 0 is a real setting, not a floor to guard against: it IS markers-only. The model is hidden
// outright (not faded to invisibility, which would fade the markers with it) and the viewer is
// composited over the photo, so the markers stay at full strength. See markersOnly in
// useAutodeskPanoramaMarkers.
const MIN_PANORAMA_3D_OVERLAY_OPACITY = 0;
const MAX_PANORAMA_3D_OVERLAY_OPACITY = 1;

export const clampPanorama3DOverlayOpacity = (value: number): number => (
  Math.min(MAX_PANORAMA_3D_OVERLAY_OPACITY, Math.max(MIN_PANORAMA_3D_OVERLAY_OPACITY, value))
);

/** The state "legs" the overlay's visibility stands on — all owned by App. */
export interface Panorama3DOverlayLegs {
  isPanoramaPage: boolean;
  showAutodeskViewer: boolean;
  hasAutodeskConfig: boolean;
  show3DViewer: boolean;
  hasIFCFile: boolean;
}

/** Pure: which overlay (if any) can actually render right now. */
export const derivePanorama3DOverlayVisibility = (
  showOverlay: boolean,
  source: Panorama3DOverlaySource | null,
  legs: Panorama3DOverlayLegs
): { autodeskVisible: boolean; ifcVisible: boolean; visible: boolean } => {
  const autodeskVisible =
    legs.isPanoramaPage &&
    showOverlay &&
    source === 'autodesk' &&
    legs.showAutodeskViewer &&
    legs.hasAutodeskConfig;
  const ifcVisible =
    legs.isPanoramaPage &&
    showOverlay &&
    source === 'ifc' &&
    legs.show3DViewer &&
    legs.hasIFCFile;
  return { autodeskVisible, ifcVisible, visible: autodeskVisible || ifcVisible };
};

/**
 * Pure: THE invariant. A stored-on flag must be cleared when it can no longer mean
 * anything: the user left the panorama page, the flagged source's legs died, or the
 * flag is on with no source at all (incoherent). Guarantees flag=true ⇒ derived
 * visibility=true (after the reconciler pass) — the property whose absence shipped
 * the wedged-overlay bug.
 */
export const shouldClearOverlayFlag = (
  showOverlay: boolean,
  source: Panorama3DOverlaySource | null,
  legs: Panorama3DOverlayLegs
): boolean => {
  if (!showOverlay) return false;
  if (!legs.isPanoramaPage) return true; // pre-existing page-leave close, folded in
  if (source === 'autodesk') return !legs.showAutodeskViewer || !legs.hasAutodeskConfig;
  if (source === 'ifc') return !legs.show3DViewer || !legs.hasIFCFile;
  return true; // flag on with no source — incoherent, clear defensively
};

export const usePanorama3DOverlay = (legs: Panorama3DOverlayLegs) => {
  // App rebuilds the legs object every render. Re-identify it from the leg VALUES so the
  // reconciler below can depend on the whole object (and so a newly added leg can't be
  // silently left out of its dep list) without firing on every parent render.
  const {
    isPanoramaPage, showAutodeskViewer, hasAutodeskConfig, show3DViewer, hasIFCFile,
  } = legs;
  const stableLegs = useMemo<Panorama3DOverlayLegs>(
    () => ({ isPanoramaPage, showAutodeskViewer, hasAutodeskConfig, show3DViewer, hasIFCFile }),
    [isPanoramaPage, showAutodeskViewer, hasAutodeskConfig, show3DViewer, hasIFCFile]
  );

  const [showPanorama3DOverlay, setShowPanorama3DOverlay] = useState(false);
  const [panorama3DOverlaySource, setPanorama3DOverlaySource] =
    useState<Panorama3DOverlaySource | null>(null);
  const [panorama3DOverlayOpacity, setPanorama3DOverlayOpacity] =
    useState(DEFAULT_PANORAMA_3D_OVERLAY_OPACITY);

  const {
    autodeskVisible: isAutodeskPanorama3DOverlayVisible,
    ifcVisible: isIFCPanorama3DOverlayVisible,
    visible: isPanorama3DOverlayVisible,
  } = derivePanorama3DOverlayVisibility(showPanorama3DOverlay, panorama3DOverlaySource, legs);

  const closePanorama3DOverlay = useCallback(() => {
    setShowPanorama3DOverlay(false);
    setPanorama3DOverlaySource(null);
  }, []);

  /**
   * Turn the overlay on for a source whose activation App has already performed.
   *
   * The opacity is deliberately NOT reset here. It is a user setting, and its bottom end is
   * markers-only — resetting on every open meant someone who wants flags alone on the panorama
   * had to drag back to 0 each time the overlay reopened. The initial state already supplies
   * DEFAULT_PANORAMA_3D_OVERLAY_OPACITY for a first-ever open.
   */
  const openPanorama3DOverlay = useCallback((source: Panorama3DOverlaySource) => {
    setPanorama3DOverlaySource(source);
    setShowPanorama3DOverlay(true);
    // Opposite intent: showing the model is not markers-only.
  }, []);

  // Reconciler — see shouldClearOverlayFlag. Warn only for the on-page leg-death case
  // (a genuine anomaly signal); the page-leave close is normal navigation, no noise.
  useEffect(() => {
    if (!shouldClearOverlayFlag(showPanorama3DOverlay, panorama3DOverlaySource, stableLegs)) return;
    if (stableLegs.isPanoramaPage) {
      logger.warn(
        panorama3DOverlaySource === null
          ? '[PanoramaNav] overlay flag on with no source (incoherent write) — clearing'
          : `[PanoramaNav] overlay legs died (source=${panorama3DOverlaySource}) — clearing wedged overlay flag`
      );
    }
    closePanorama3DOverlay();
  }, [showPanorama3DOverlay, panorama3DOverlaySource, stableLegs, closePanorama3DOverlay]);

  const panorama3DOverlayOpacityPercent = Math.round(panorama3DOverlayOpacity * 100);

  const handlePanorama3DOverlayOpacityChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const nextPercent = Number(event.target.value);
    if (!Number.isFinite(nextPercent)) {
      return;
    }
    setPanorama3DOverlayOpacity(clampPanorama3DOverlayOpacity(nextPercent / 100));
  }, []);

  return {
    showPanorama3DOverlay,
    panorama3DOverlaySource,
    panorama3DOverlayOpacity,
    panorama3DOverlayOpacityPercent,
    handlePanorama3DOverlayOpacityChange,
    isAutodeskPanorama3DOverlayVisible,
    isIFCPanorama3DOverlayVisible,
    isPanorama3DOverlayVisible,
    openPanorama3DOverlay,
    closePanorama3DOverlay,
    // DERIVED from the slider: the bottom of its range is not "almost invisible", it is
    // markers-only. Fading the model to 0 with CSS would take the markers with it, so at 0 the
    // model is hidden properly and the viewer composites over the photo instead.
    isPanoramaMarkersOnly: isPanorama3DOverlayVisible && panorama3DOverlayOpacity <= 0,
  };
};
